import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import { useNavigate } from 'react-router-dom';
import { sellerService } from '../../services/sellerService';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/ui/Icon';
import Footer from '../homepage/components/Footer';
import { useAuth } from '../../contexts/AuthContext';

import { userPaymentMethodService } from '../../services/userPaymentMethodService';
import StripePaymentForm from '../../components/ui/StripePaymentForm';

export default function SellerSignup() {
  const navigate = useNavigate();
  const { user: currentUser, isSeller, refreshUserProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [approvalMessage, setApprovalMessage] = useState('');
  
  // Auth data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  // Business data
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  
  // Subscription data
  const [selectedTier, setSelectedTier] = useState('entry');
  
  // Terms and Agreements
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [activeTerms, setActiveTerms] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Email confirmation state
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [pendingSignupData, setPendingSignupData] = useState(null);

  // New Seller User ID
  const [createdUserId, setCreatedUserId] = useState(null);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const terms = await sellerService.getActiveTermsVersion();
        setActiveTerms(terms);
      } catch (err) {
        console.error('Failed to load terms:', err);
      }
    };
    fetchTerms();
  }, []);

  // If already a seller, redirect
  useEffect(() => {
    if (isSeller) {
      navigate('/seller-dashboard');
    }
  }, [isSeller, navigate]);

  // If logged in as buyer, skip to business info
  useEffect(() => {
    if (currentUser && !isSeller && step === 1) {
      setStep(2);
      setEmail(currentUser.email || '');
      setBusinessEmail(currentUser.email || '');
      setFirstName(currentUser.user_metadata?.first_name || '');
      setLastName(currentUser.user_metadata?.last_name || '');
    }
  }, [currentUser, isSeller, step]);

  // Check for pending signup on mount
  useEffect(() => {
    const checkPendingSignup = () => {
      const saved = localStorage.getItem('pendingSellerSignup');
      if (saved) {
        try {
          const data = JSON.parse(saved);
          
          // Only offer resume if the attempt is fresh (less than 60 minutes old)
          const attemptTime = new Date(data.timestamp).getTime();
          const now = new Date().getTime();
          const minutesPassed = (now - attemptTime) / (1000 * 60);

          if (minutesPassed < 60) {
            setPendingSignupData(data);
            setEmail(data?.email || '');
            // We no longer setAwaitingConfirmation(true) automatically
            // This allows the user to start fresh if they want
          } else {
            localStorage.removeItem('pendingSellerSignup');
          }
        } catch (err) {
          localStorage.removeItem('pendingSellerSignup');
        }
      }
    };
    checkPendingSignup();
  }, []);

  const tiers = [
    {
      id: 'entry',
      name: 'Entry Level',
      price: 14.99,
      description: 'Perfect for getting started',
      features: [
        'Manage your own order fulfillment',
        'Upload and manage your 3D models',
        'Basic analytics dashboard',
        'Standard seller support'
      ]
    },
    {
      id: 'mid_tier',
      name: 'Mid-Tier',
      price: 24.99,
      description: 'Scale your business with suppliers',
      features: [
        'All Entry Level features',
        'Third-party supplier fulfillment',
        'Advanced analytics and reporting',
        'Priority seller support',
        'Bulk product uploads'
      ],
      popular: true
    },
    {
      id: 'platinum',
      name: 'Platinum',
      price: 34.99,
      description: 'Premium features and dedicated support',
      features: [
        'All Mid-Tier features',
        'Dedicated Strategic Account Manager',
        'Premium placement in search results',
        'Custom branding options',
        'API access for automation',
        'White-glove onboarding'
      ]
    }
  ];

  const handleAuthSignup = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (!firstName || !lastName || !email || !password) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate password strength
    if (password?.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Move to next step
    setStep(2);
    setBusinessEmail(email);
  };

  const handleBusinessInfo = async (e) => {
      e?.preventDefault();
      setError('');
      
      if (!businessName || !businessEmail) {
        setError('Please fill in all required fields');
        return;
      }
      
      setLoading(true);
      try {
          const { data: existingStore, error: storeErr } = await supabase
            .from('seller_accounts')
            .select('id')
            .ilike('business_name', businessName.trim())
            .maybeSingle();
            
          if (storeErr) throw storeErr;
          
          if (existingStore) {
              setError('This store name is already in use. Please choose another.');
              setLoading(false);
              return;
          }
      } catch (err) {
          console.error("Store name verification error:", err);
          setError('Failed to verify store name availability.');
          setLoading(false);
          return;
      }
      setLoading(false);
      setStep(3);
    };

  const handleSubscriptionWithPayment = async (paymentData) => {
    setError('');
    setSuccessMessage('');

    if (activeTerms && !agreedToTerms) {
       setError('You must agree to the Terms and Conditions to proceed.');
       return;
    }

    setLoading(true);

    try {
      // Step 0: If already logged in as buyer, skip auth creation
      if (currentUser && !isSeller) {
        const accountData = {
          businessName,
          businessEmail,
          businessPhone,
          tier: selectedTier
        };

        // Create seller account
        await sellerService?.createAccount(accountData);
        setCreatedUserId(currentUser.id);
        
        // Save T&C
        if (activeTerms && agreedToTerms) {
            await sellerService.recordSellerAgreement(currentUser.id, activeTerms.id).catch(e => console.error("T&C save error", e));
        }
        
        // Save Payment Method
        const businessPaymentData = { ...paymentData, isBusiness: true };
        await userPaymentMethodService.createPaymentMethod(currentUser.id, businessPaymentData).catch(e => console.error("Payment save error", e));

        localStorage.removeItem('pendingSellerSignup');
        
        setApprovalMessage('Your payment method has been saved! You can start configuring your storefront while the business approval takes place, which can take 5-7 business days depending on the completeness of information and check verifications, and then update your account to a seller account.');
        
        if (refreshUserProfile) {
            await refreshUserProfile();
        }
        
        setTimeout(() => {
            navigate('/seller-dashboard');
        }, 8000);
        return;
      }

      // Step 1: Check if email already exists
      const { data: existingUsers } = await supabase
        ?.from('users')
        ?.select('id, email')
        ?.eq('email', email)
        ?.maybeSingle();

      if (existingUsers) {
        const { data: existingAccount } = await supabase
          ?.from('seller_accounts')
          ?.select('id')
          ?.eq('user_id', existingUsers?.id)
          ?.maybeSingle();

        if (existingAccount) {
          setError('This email is already registered as a seller. Please sign in instead.');
          setLoading(false);
          return;
        }
        setError('An account with this email already exists. If you have confirmed your email, please sign in to complete seller registration.');
        setLoading(false);
        return;
      }

      // Step 2: Create auth account
      const { data: signupData, error: signupError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            role: 'seller'
          }
        }
      });

      if (signupError) {
        if (signupError?.message?.includes('already registered')) {
          setError('This email is already registered. Please sign in or use a different email.');
        } else {
          setError(signupError?.message || 'Failed to create account');
        }
        setLoading(false);
        return;
      }

      if (!signupData?.user) {
        throw new Error('User creation failed - no user data returned');
      }

      // Step 3: Check if we have a session (email confirmation disabled) or need confirmation
      if (signupData?.session) {
        const accountData = {
          businessName,
          businessEmail,
          businessPhone,
          tier: selectedTier
        };

        await sellerService?.createAccount(accountData);
        setCreatedUserId(signupData.user.id);

        if (activeTerms && agreedToTerms) {
            await sellerService.recordSellerAgreement(signupData.user.id, activeTerms.id).catch(e => console.error("T&C save error", e));
        }

        const businessPaymentData = { ...paymentData, isBusiness: true };
        await userPaymentMethodService.createPaymentMethod(signupData.user.id, businessPaymentData).catch(e => console.error("Payment save error", e));

        localStorage.removeItem('pendingSellerSignup');
        
        setApprovalMessage('Your payment method has been saved! You can start configuring your storefront while the business approval takes place, which can take 5-7 business days depending on the completeness of information and check verifications, and then update your account to a seller account.');
        
        if (refreshUserProfile) {
            await refreshUserProfile();
        }
        
        setTimeout(() => {
            navigate('/seller-dashboard');
        }, 8000);
      } else {
        // Email confirmation required - save signup data
        const signupDataObj = {
          email,
          firstName,
          lastName,
          businessName,
          businessEmail,
          businessPhone,
          tier: selectedTier,
          timestamp: new Date()?.toISOString(),
          paymentData: { ...paymentData, isBusiness: true },
          agreedTermsId: activeTerms?.id
        };

        localStorage.setItem('pendingSellerSignup', JSON.stringify(signupDataObj));
        setPendingSignupData(signupDataObj);
        setAwaitingConfirmation(true);
        
        setSuccessMessage('Account created! Please check your email to confirm your account. After confirming, return here to complete your seller registration.');
      }
    } catch (err) {
      console.error('Error in seller signup flow:', err);
      setError(err?.message || 'Failed to complete signup');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSignup = async () => {
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      // Check if user is now authenticated (email confirmed)
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError || !user) {
        setError('Please confirm your email first. Check your inbox for the confirmation link.');
        setLoading(false);
        return;
      }

      // Check if seller account already exists
      const existingAccount = await sellerService?.getSellerAccountForUser(user.id);
      
      if (existingAccount) {
        setSuccessMessage('Your seller account is already set up! Redirecting...');
        localStorage.removeItem('pendingSellerSignup');
        setTimeout(() => {
          navigate('/seller-dashboard');
        }, 2000);
        return;
      }

      // Create seller account with saved data
      const accountData = {
        businessName: pendingSignupData?.businessName,
        businessEmail: pendingSignupData?.businessEmail,
        businessPhone: pendingSignupData?.businessPhone,
        tier: pendingSignupData?.tier
      };

      await sellerService?.createAccount(accountData);
      
      if (pendingSignupData?.agreedTermsId) {
          await sellerService.recordSellerAgreement(user.id, pendingSignupData.agreedTermsId).catch(e => console.error("T&C save error", e));
      }

      if (pendingSignupData?.paymentData) {
          await userPaymentMethodService.createPaymentMethod(user.id, pendingSignupData.paymentData).catch(e => console.error("Payment save error", e));
      }

      setCreatedUserId(user.id);
      localStorage.removeItem('pendingSellerSignup');
      setApprovalMessage('Your payment method has been saved! You can start configuring your storefront while the business approval takes place, which can take 5-7 business days depending on the completeness of information and check verifications, and then update your account to a seller account.');
      
      if (refreshUserProfile) {
          await refreshUserProfile();
      }
      
      setTimeout(() => {
          navigate('/seller-dashboard');
      }, 8000);
    } catch (err) {
      console.error('Error completing signup:', err);
      setError(err?.message || 'Failed to complete signup. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: pendingSignupData?.email || email
      });

      if (error) throw error;
      
      setSuccessMessage('Confirmation email resent! Please check your inbox.');
    } catch (err) {
      console.error('Error resending confirmation:', err);
      setError(err?.message || 'Failed to resend confirmation email');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = () => {
    localStorage.removeItem('pendingSellerSignup');
    setAwaitingConfirmation(false);
    setPendingSignupData(null);
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setBusinessName('');
    setBusinessEmail('');
    setBusinessPhone('');
    setSelectedTier('entry');
    setStep(1);
    setError('');
    setSuccessMessage('');
  };

  // Render email confirmation screen
  if (awaitingConfirmation) {
    return (
      <>
        <Header />
        <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="text-center mb-8">
                <Icon name="Mail" size={64} className="mx-auto text-blue-600 mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Confirm Your Email</h2>
                <p className="text-gray-600">
                  We've sent a confirmation email to <span className="font-semibold">{pendingSignupData?.email || email}</span>
                </p>
                {window.location.hostname === 'localhost' && (
                  <div className="mt-4 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs italic">
                    Note: In local development, emails are only sent if AWS SES is configured. 
                    Disable "Confirm Email" in Supabase Auth settings to bypass this.
                  </div>
                )}
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  {successMessage}
                </div>
              )}

              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Next Steps:</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li>Check your email inbox for a confirmation link</li>
                    <li>Click the confirmation link in the email</li>
                    <li>Return to this page and click "Complete Signup"</li>
                  </ol>
                </div>

                <Button
                  onClick={handleCompleteSignup}
                  disabled={loading}
                  className="w-full"
                  iconName={loading ? "Loader2" : "CheckCircle"}
                  iconClassName={loading ? "animate-spin" : ""}
                  iconPosition="right"
                >
                  {loading ? 'Checking...' : 'Complete Signup'}
                </Button>

                <Button
                  onClick={handleResendConfirmation}
                  disabled={loading}
                  className="w-full bg-gray-600 hover:bg-gray-700"
                  iconName={loading ? "Loader2" : "Mail"}
                  iconClassName={loading ? "animate-spin" : ""}
                  iconPosition="right"
                >
                  {loading ? 'Sending...' : 'Resend Confirmation Email'}
                </Button>

                <button
                  onClick={handleStartOver}
                  className="w-full text-center text-blue-600 hover:text-blue-700 font-medium py-2"
                >
                  Start Over with Different Email
                </button>

                <p className="text-sm text-center text-gray-600 mt-4">
                  Already confirmed?{' '}
                  <button
                    onClick={() => navigate('/signin')}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="bg-gray-50 pt-36 pb-12 px-4 sm:px-6 lg:px-8 mt-10">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-6">
            <div className="flex items-center justify-center space-x-4">
              {[1, 2, 3]?.map((num) => (
                <div key={num} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    step >= num ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {num}
                  </div>
                  {num < 3 && (
                    <div className={`w-16 h-1 mx-2 ${
                      step > num ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-4 space-x-24 text-sm">
              <span className={step >= 1 ? 'text-blue-600 font-medium' : 'text-gray-500'}>
                Account
              </span>
              <span className={step >= 2 ? 'text-blue-600 font-medium' : 'text-gray-500'}>
                Business Info
              </span>
              <span className={step >= 3 ? 'text-blue-600 font-medium' : 'text-gray-500'}>
                Subscription
              </span>
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            {/* Resume Banner */}
            {pendingSignupData && !awaitingConfirmation && step === 1 && (
              <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center">
                  <Icon name="Info" className="text-blue-600 mr-3" size={20} />
                  <p className="text-sm text-blue-800">
                    You have a pending application for <span className="font-bold">{pendingSignupData.email}</span>.
                  </p>
                </div>
                <div className="flex gap-3 text-left">
                  <button 
                    onClick={() => setAwaitingConfirmation(true)}
                    className="text-sm font-bold text-blue-600 hover:underline"
                  >
                    Resume Application
                  </button>
                  <button 
                    onClick={() => {
                      localStorage.removeItem('pendingSellerSignup');
                      setPendingSignupData(null);
                    }}
                    className="text-sm font-bold text-gray-500 hover:underline"
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Account Creation */}
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Create Your Seller Account</h2>
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}
                {successMessage && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                    {successMessage}
                  </div>
                )}
                <form onSubmit={handleAuthSignup} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e?.target?.value)}
                      required
                      placeholder="John"
                    />
                    <Input
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e?.target?.value)}
                      required
                      placeholder="Doe"
                    />
                  </div>
                  <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    required
                    placeholder="seller@example.com"
                  />
                  <Input
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    required
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full"
                    iconName={loading ? "Loader2" : "ArrowRight"}
                    iconClassName={loading ? "animate-spin" : ""}
                    iconPosition="right"
                  >
                    {loading ? 'Processing...' : 'Continue'}
                  </Button>
                </form>
                <p className="mt-4 text-sm text-center text-gray-600">
                  Already have an account?{' '}
                  <button
                    onClick={() => navigate('/signin')}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            )}

            {/* Step 2: Business Information */}
            {step === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Business Information</h2>
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}
                <form onSubmit={handleBusinessInfo} className="space-y-4">
                  <Input
                    label="Business Name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e?.target?.value)}
                    required
                    placeholder="Your Business Name"
                  />
                  <Input
                    label="Business Email"
                    type="email"
                    value={businessEmail}
                    onChange={(e) => setBusinessEmail(e?.target?.value)}
                    required
                    placeholder="contact@business.com"
                  />
                  <Input
                    label="Business Phone (Optional)"
                    type="tel"
                    value={businessPhone}
                    onChange={(e) => setBusinessPhone(e?.target?.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                  <div className="flex space-x-4">
                    <Button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1"
                      iconName="ArrowLeft"
                      iconPosition="left"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      Continue
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Step 3: Subscription Selection */}
            {step === 3 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
                <p className="text-gray-600 mb-6">Select the subscription tier that best fits your needs</p>
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}
                {successMessage && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                    {successMessage}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {tiers?.map((tier) => (
                    <div
                      key={tier?.id}
                      onClick={() => setSelectedTier(tier?.id)}
                      className={`relative p-6 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedTier === tier?.id
                          ? 'border-blue-600 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {tier?.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                            POPULAR
                          </span>
                        </div>
                      )}
                      
                      <div className="text-center mb-4">
                        <h3 className="text-lg font-bold text-gray-900">{tier?.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{tier?.description}</p>
                        <div className="mt-4">
                          <span className="text-3xl font-bold text-gray-900">${tier?.price}</span>
                          <span className="text-gray-600">/month</span>
                        </div>
                      </div>

                      <ul className="space-y-3">
                        {tier?.features?.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <Icon name="Check" size={20} className="text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {selectedTier === tier?.id && (
                        <div className="absolute top-4 right-4">
                          <Icon name="CheckCircle" size={24} className="text-blue-600" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Add Payment Method</h3>
                  <p className="text-sm text-gray-600 mb-4">Enter a credit card to bill your monthly subscription.</p>
                  
                  <StripePaymentForm 
                    userEmail={businessEmail || email}
                    defaultNickname="Business Subscription Card"
                    buttonText={loading ? "Processing..." : "Complete Signup"}
                    disabled={loading || (activeTerms && !agreedToTerms)}
                    onSuccess={handleSubscriptionWithPayment}
                    onCancel={() => setStep(2)}
                  >
                    {activeTerms && (
                      <div className="mt-6 mb-4 bg-white p-4 rounded border border-gray-200">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="terms"
                              type="checkbox"
                              className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                              checked={agreedToTerms}
                              onChange={(e) => setAgreedToTerms(e.target.checked)}
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="terms" className="font-medium text-gray-900 cursor-pointer">
                              I agree to the Seller Terms & Conditions
                            </label>
                            <div 
                              className="text-gray-500 mt-1 line-clamp-2 text-xs"
                              dangerouslySetInnerHTML={{ __html: activeTerms.content?.replace(/<[^>]*>?/gm, '') }}
                            />
                            <button 
                              type="button" 
                              onClick={() => setShowTermsModal(true)} 
                              className="text-blue-600 hover:text-blue-800 text-xs font-medium mt-1 underline"
                            >
                              Read Full Terms
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </StripePaymentForm>

                  {approvalMessage && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 font-medium">
                      <Icon name="Info" className="inline mr-2" />
                      {approvalMessage}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />

      {/* Terms Modal */}
      {showTermsModal && activeTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-4">
               <h2 className="text-xl font-bold">Seller Terms & Conditions</h2>
               <button onClick={() => setShowTermsModal(false)} className="text-gray-400 hover:text-gray-600">
                 <Icon name="X" size={24} />
               </button>
            </div>
            <div 
              className="flex-1 overflow-y-auto p-4 bg-gray-50 border border-gray-200 rounded whitespace-pre-wrap text-sm text-gray-700 prose max-w-none"
              dangerouslySetInnerHTML={{ __html: activeTerms.content }}
            />
            <div className="mt-6 flex justify-end gap-3">
              <Button 
                variant="outline"
                onClick={() => {
                   const element = document.createElement("a");
                   const file = new Blob([activeTerms.content?.replace(/<[^>]*>?/gm, '')], {type: 'text/plain'});
                   element.href = URL.createObjectURL(file);
                   element.download = "OpenGadget_Seller_Terms.txt";
                   document.body.appendChild(element);
                   element.click();
                   document.body.removeChild(element);
                }}
                iconName="Download"
                iconPosition="left"
              >
                Save Copy
              </Button>
              <Button onClick={() => setShowTermsModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}